/*
  The view of a look in Your Private Edit (stage א9, note 3): <amor-look-view>
  (snippets/look-view) shows one frame with two modes, "on a model" and "on the
  avatar", from the pool in [data-look-view-pool] (snippets/look-view-pool).
  - The view is picked by the look: women or men (more men's items than
    women's: men; a tie: women), and a dress or trousers (a dress in the look:
    dress; every men's view is trousers). Within those, by a number made from
    the look's name and items, so the same look always gets the same view, also
    after a refresh. A later look on the page skips a view an earlier look has.
  - Dots: one per zone of the body (head, top, bottom, shoes, bag,
    accessories), for the look's item in that zone, and only where the view
    shows that zone. A dress takes the top, or the bottom when a top is there.
    One item per zone, the latest wins, like the avatar. An item she already
    owns gets no dot (no quick add, like its card).
  - A dot is the quick add's button (components.js listens for [data-quick-add]).
  - Stage א10 (note 3): an avatar's pieces are placed by hand in the pool (a box
    for each, the left shoe mirrored), and its dots are listed there, like a
    photo's, on the pieces' new places.
  AmorLookView.assign(looks) picks the views of all the looks on the page at
  once, in their order: [{ element, name, items }], items as loadItem gives them.
*/
(() => {
  const ZONE = {
    hat: 'head',
    eyes: 'head',
    top: 'top',
    bottom: 'bottom',
    dress: 'dress',
    shoes: 'shoes',
    bag: 'bag',
    neck: 'accessories',
    waist: 'accessories',
    side: 'accessories',
  };

  let pool = null;
  const readPool = () => {
    if (pool) return pool;
    try {
      pool = JSON.parse(document.querySelector('[data-look-view-pool]')?.textContent || 'null');
    } catch {
      pool = null;
    }
    return pool;
  };

  /** A small stable number from a text (djb2). */
  const hash = (text) => {
    let value = 5381;
    for (let i = 0; i < text.length; i += 1) value = ((value << 5) + value + text.charCodeAt(i)) >>> 0;
    return value;
  };

  const placed = (items) => items.filter((item) => item.avatar && ZONE[item.avatar.s]);

  /** Women or men, and a dress or trousers. */
  const kindOf = (items) => {
    const counted = placed(items);
    const men = counted.filter((item) => item.avatar.w === 'men').length;
    const women = counted.filter((item) => item.avatar.w === 'women').length;
    const world = men > women ? 'men' : 'women';
    const dress = world === 'women' && counted.some((item) => item.avatar.s === 'dress');
    return { world, shape: dress ? 'dress' : 'trousers' };
  };

  /** The look's item in each zone: the latest wins; a dress takes the top, or else the bottom. */
  const zonesOf = (items) => {
    const zones = {};
    const dresses = [];
    placed(items)
      .filter((item) => !item.owned)
      .forEach((item) => {
        const zone = ZONE[item.avatar.s];
        if (zone === 'dress') dresses.push(item);
        else zones[zone] = item;
      });
    dresses.reverse().forEach((item) => {
      if (!zones.top) zones.top = item;
      else if (!zones.bottom) zones.bottom = item;
    });
    return zones;
  };

  class AmorLookView extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      this.ready = true;
      this.addEventListener('click', (event) => {
        const option = event.target.closest('[data-look-view-mode]');
        if (option) this.setMode(option.dataset.lookViewMode);
      });
    }

    setMode(mode) {
      this.dataset.mode = mode;
      this.querySelectorAll('[data-look-view-mode]').forEach((option) => {
        option.setAttribute('aria-pressed', String(option.dataset.lookViewMode === mode));
      });
      this.querySelectorAll('[data-look-view-panel]').forEach((panel) => {
        panel.hidden = panel.dataset.lookViewPanel !== mode;
      });
    }

    /** Draws the look on its two views: { model, avatar } from the pool, the look's zones and world. */
    show({ model, avatar, zones, world, files }) {
      const label = this.querySelector('[data-look-view-model-label]');
      if (label) label.textContent = world === 'men' ? this.dataset.labelModelMen : this.dataset.labelModelWomen;

      const panelModel = this.querySelector('[data-look-view-panel="model"]');
      const panelAvatar = this.querySelector('[data-look-view-panel="avatar"]');
      const photo = panelModel?.querySelector('[data-look-view-photo]');
      if (photo && model) {
        photo.src = model.src;
        photo.hidden = false;
      }
      if (avatar) {
        // The combination is fixed and only a picture: no handle, so no quick add on its items.
        const element = panelAvatar?.querySelector('amor-avatar');
        // Stage א10 (note 3): each piece placed by hand, its box, mirror, turn and layer.
        element?.setItems(
          avatar.items.map((item) => ({ s: item.s, w: avatar.world, i: `${files}${item.file}?width=600`, t: '', p: item.p, f: item.flip, r: item.r, z: item.z })),
        );
      }
      this.drawDots(panelModel, model ? model.dots : {}, zones);
      this.drawDots(panelAvatar, avatar ? avatar.dots || {} : {}, zones);
    }

    drawDots(panel, dots, zones) {
      const layer = panel?.querySelector('[data-look-view-dots]');
      if (!layer) return;
      const buttons = Object.entries(zones)
        .filter(([zone]) => dots[zone])
        .map(([zone, item]) => {
          const [x, y] = dots[zone];
          const button = document.createElement('button');
          button.type = 'button';
          button.className = 'look-view__dot';
          button.style.setProperty('--x', `${x}%`);
          button.style.setProperty('--y', `${y}%`);
          button.dataset.zone = zone;
          button.dataset.quickAdd = item.avatar.h;
          if (item.avatar.v) button.dataset.quickAddVariant = item.avatar.v;
          if (item.avatar.u) button.dataset.fallbackUrl = item.avatar.u;
          button.setAttribute('aria-label', (this.dataset.labelQuickAdd || '__TITLE__').replace('__TITLE__', item.avatar.t || ''));
          const mark = document.createElement('span');
          mark.className = 'look-view__mark';
          mark.setAttribute('aria-hidden', 'true');
          button.append(mark);
          return button;
        });
      layer.replaceChildren(...buttons);
    }
  }

  customElements.define('amor-look-view', AmorLookView);

  /** Picks the views of every look on the page, in their order, and draws them. */
  const assign = (looks) => {
    const data = readPool();
    if (!data) return;
    const used = { model: new Set(), avatar: new Set() };
    looks.forEach(({ element, name, items }) => {
      if (!element) return;
      const kind = kindOf(items);
      const key = `${name || ''}|${placed(items)
        .map((item) => item.handle)
        .join(',')}`;
      const pick = (mode) => {
        const views = (data[mode] || []).filter((view) => view.world === kind.world && view.shape === kind.shape);
        if (!views.length) return null;
        const start = hash(key) % views.length;
        for (let step = 0; step < views.length; step += 1) {
          const view = views[(start + step) % views.length];
          if (!used[mode].has(view.id)) {
            used[mode].add(view.id);
            return view;
          }
        }
        return views[start];
      };
      element.show({ model: pick('model'), avatar: pick('avatar'), zones: zonesOf(items), world: kind.world, files: data.files });
    });
  };

  window.AmorLookView = { assign };
})();
